import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import CategorySidebar from "@/components/feature/CategorySidebar";
import SectionHeading from "@/components/base/SectionHeading";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import Badge from "@/components/base/Badge";
import { NewsListCard } from "@/components/feature/ContentCards";
import { newsCategories, newsList } from "@/mocks/content";
import { searchContent, shortDate } from "@/lib/content";
import type { ContentKind } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 5;

const categoryIntro: Record<string, string> = {
  全部: "汇总影视、游戏、漫画与行业动态的最新报道，按时间与热度自由筛选。",
  影视资讯: "聚焦成年向电影与剧集的制作、上线与评论，包含适看提示与观看建议。",
  游戏动态: "文字冒险、叙事游戏与独立新作的版本更新、玩法解析与开发者访谈。",
  漫画观察: "单行本、连载与短篇的分镜分析、出版消息与作者动向。",
  行业观察: "分级制度、平台规则与创作边界的行业讨论，提供可参考的背景信息。",
};

const sortOptions = [
  { key: "latest", label: "最新发布" },
  { key: "hottest", label: "热门优先" },
  { key: "oldest", label: "最早发布" },
];

const searchKindOptions: Array<{ key: "all" | ContentKind; label: string }> = [
  { key: "all", label: "全部结果" },
  { key: "news", label: "资讯" },
  { key: "resource", label: "资源" },
  { key: "topic", label: "话题" },
];

export default function NewsPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = (searchParams.get("q") ?? "").trim();

  const activeCategory = !category || category === "all" ? "全部" : decodeURIComponent(category);
  const isSearchMode = query.length > 0;

  const [typeFilter, setTypeFilter] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [searchKind, setSearchKind] = useState<"all" | ContentKind>("all");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [activeCategory, typeFilter, sort, query, searchKind]);

  useDocumentMeta({
    title: isSearchMode
      ? `搜索「${query}」 - 18禁成人内容平台`
      : `${activeCategory === "全部" ? "资讯中心" : activeCategory} - 18禁成人内容平台`,
    description: isSearchMode
      ? `18禁站内搜索「${query}」的结果，覆盖资讯、资源与话题讨论，支持按内容类型筛选。`
      : `${activeCategory === "全部" ? "全部资讯" : activeCategory}：${categoryIntro[activeCategory] ?? categoryIntro["全部"]}`,
    keywords: `${activeCategory},18禁资讯,成人内容资讯,${activeCategory === "全部" ? "影视资讯,游戏动态,漫画观察" : activeCategory}`,
  });

  const searched = useMemo(() => (isSearchMode ? searchContent(query) : []), [isSearchMode, query]);

  const newsItems = useMemo(() => {
    const base = activeCategory === "全部" ? newsList : newsList.filter((item) => item.category === activeCategory);
    const filtered = typeFilter === "全部" ? base : base.filter((item) => item.category === typeFilter);
    const sorted = [...filtered];
    if (sort === "latest") sorted.sort((a, b) => b.date.localeCompare(a.date));
    if (sort === "oldest") sorted.sort((a, b) => a.date.localeCompare(b.date));
    if (sort === "hottest") sorted.sort((a, b) => b.hot - a.hot);
    return sorted;
  }, [activeCategory, typeFilter, sort]);

  const searchResults = useMemo(() => {
    const filtered = searchKind === "all" ? searched : searched.filter((item) => item.kind === searchKind);
    return [...filtered].sort((a, b) => b.date.localeCompare(a.date));
  }, [searched, searchKind]);

  const listLength = isSearchMode ? searchResults.length : newsItems.length;
  const totalPages = Math.max(1, Math.ceil(listLength / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedNews = newsItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pagedResults = searchResults.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setTypeFilter("全部");
    setSort("latest");
    setSearchKind("all");
    setPage(1);
  };

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <Link to="/news" className="cursor-pointer transition hover:text-primary-700">
              资讯
            </Link>
            {isSearchMode ? (
              <>
                <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
                <span className="text-foreground-700">搜索结果</span>
              </>
            ) : activeCategory !== "全部" ? (
              <>
                <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
                <span className="text-foreground-700">{activeCategory}</span>
              </>
            ) : null}
          </nav>

          <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[32px]">
            {isSearchMode ? `「${query}」的搜索结果` : activeCategory === "全部" ? "资讯中心" : activeCategory}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {isSearchMode
              ? `在资讯、资源与话题中检索到 ${searched.length} 条相关内容，可按内容类型进一步筛选。`
              : categoryIntro[activeCategory] ?? categoryIntro["全部"]}
          </p>
          {isSearchMode ? (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">搜索类型</span>
              {searchKindOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSearchKind(option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition ${
                    searchKind === option.key
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {option.label}
                  {option.key !== "all"
                    ? ` ${searched.filter((item) => item.kind === option.key).length}`
                    : ` ${searched.length}`}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid gap-8 lg:grid-cols-[248px_1fr] lg:gap-10">
          <aside className="lg:sticky lg:top-[118px] lg:self-start">
            <CategorySidebar />
          </aside>

          <div className="min-w-0">
            {isSearchMode ? (
              <div>
                <SectionHeading
                  eyebrow="SEARCH RESULTS"
                  title={`共 ${searchResults.length} 条结果`}
                  description="结果覆盖资讯报道、资源条目与话题讨论，点击任一卡片进入详情。"
                />
                {pagedResults.length ? (
                  <div className="space-y-3">
                    {pagedResults.map((item) => (
                      <Link
                        key={`${item.kind}-${item.id}`}
                        to={item.href}
                        className="group flex cursor-pointer gap-4 rounded-lg border border-background-200/90 bg-background-50 p-3 transition hover:border-primary-200 hover:bg-primary-50/30 sm:p-4"
                      >
                        <div className="relative h-[96px] w-[76px] shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[112px] sm:w-[92px]">
                          {item.cover ? (
                            <img
                              src={item.cover}
                              alt={`${item.title} 封面`}
                              title={`${item.title} - 18禁`}
                              className="h-full w-full object-top object-cover transition duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-primary-100 text-primary-700">
                              <i className="ri-discuss-line text-xl" aria-hidden="true"></i>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-1.5 flex flex-wrap items-center gap-2">
                            <Badge tone={item.kind === "resource" ? "accent" : "secondary"}>{item.label}</Badge>
                            <span className="text-[11px] text-foreground-500">{shortDate(item.date)}</span>
                          </div>
                          <h3 className="text-sm font-semibold leading-snug text-foreground-950 transition group-hover:text-primary-700">
                            {item.title}
                          </h3>
                          <p className="mt-1.5 line-clamp-2 text-[12px] leading-relaxed text-foreground-600">{item.summary}</p>
                        </div>
                      </Link>
                    ))}
                    <div className="pt-4">
                      <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    title="没有找到相关内容"
                    description={`暂时没有与「${query}」匹配的结果。可以尝试更换关键词，或切换内容类型后再看看。`}
                    actionLabel="清空搜索条件"
                    onAction={() => navigate("/news")}
                    secondaryLabel="返回资讯中心"
                    secondaryTo="/news"
                  />
                )}
              </div>
            ) : (
              <div>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-foreground-500">
                    共 <span className="font-medium text-foreground-800">{newsItems.length}</span> 篇内容
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFiltersOpen((value) => !value)}
                      className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 md:hidden"
                    >
                      <i className="ri-filter-3-line text-sm" aria-hidden="true"></i>
                      筛选与排序
                    </button>
                    <div className="hidden items-center gap-1.5 md:flex">
                      {sortOptions.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setSort(option.key)}
                          className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition ${
                            sort === option.key
                              ? "bg-primary-500 text-background-50"
                              : "text-foreground-600 hover:bg-background-100"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={`${filtersOpen ? "block" : "hidden"} mb-6 rounded-lg border border-background-200 bg-background-50 p-4 md:block`}>
                  <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-8">
                    <div className="min-w-0 flex-1">
                      <p className="mb-2.5 text-[11px] font-medium tracking-[0.16em] text-foreground-500">内容类型</p>
                      <div className="flex flex-wrap gap-1.5">
                        {newsCategories.map((item) => (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setTypeFilter(item)}
                            className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition ${
                              typeFilter === item
                                ? "border-primary-400 bg-primary-50 text-primary-700"
                                : "border-background-300/80 bg-background-50 text-foreground-600 hover:border-primary-200"
                            }`}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="min-w-0 md:w-[220px]">
                      <p className="mb-2.5 text-[11px] font-medium tracking-[0.16em] text-foreground-500">时间顺序</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sortOptions.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => setSort(option.key)}
                            className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition ${
                              sort === option.key
                                ? "border-accent-400 bg-accent-100 text-accent-800"
                                : "border-background-300/80 bg-background-50 text-foreground-600 hover:border-accent-300"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(typeFilter !== "全部" || sort !== "latest") && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-4 flex cursor-pointer items-center gap-1 text-[11px] text-foreground-500 transition hover:text-primary-700"
                    >
                      <i className="ri-refresh-line text-xs" aria-hidden="true"></i>
                      重置筛选条件
                    </button>
                  )}
                </div>

                {pagedNews.length ? (
                  <>
                    <div className="space-y-3">
                      {pagedNews.map((item) => (
                        <NewsListCard key={item.id} item={item} />
                      ))}
                    </div>
                    <div className="pt-8">
                      <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
                    </div>
                  </>
                ) : (
                  <EmptyState
                    title="该分类下暂时没有内容"
                    description="当前筛选条件下没有找到资讯。可以尝试放宽内容类型，或重置筛选条件查看全部内容。"
                    actionLabel="重置筛选条件"
                    onAction={resetFilters}
                    secondaryLabel="查看全部资讯"
                    secondaryTo="/news"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}